/* eslint-disable max-lines */
import {
  convertStatus,
  RegistrationUf,
  Status,
  ufAbbreviation,
} from '@/api/enumerations';

import {
  noticeCompanySubresourceEvents,
  noticeEventByMethod,
  noticeSubresourceEvents,
  sampleEventByMethod,
  sampleSubresourceEvents,
  workOrderEventByMethod,
  workOrderListEvents,
  workOrderSubresourceEvents,
} from './eventLabels';

const methodVerb: Record<string, string> = {
  GET: 'Visualizou',
  POST: 'Criou',
  PUT: 'Atualizou',
  PATCH: 'Atualizou',
  DELETE: 'Removeu',
};

const resourceLabels: Record<string, string> = {
  plans: 'planos',
  credits: 'cr\u00e9ditos',
  companies: 'dados da empresa',
  oversight: 'acesso delegado',
  users: 'usu\u00e1rios',
  notifications: 'notifica\u00e7\u00f5es',
  invoices: 'faturas',
  inspections: 'vistorias',
  samples: 'amostras',
  'work-orders': 'OS',
  'public-notices': 'credenciamentos',
  pictures: 'fotos',
  rooms: 'c\u00f4modos',
  attachments: 'anexos',
  forms: 'formul\u00e1rios',
  'hive-messages': 'mensagens do Hive',
  'notice-companies': 'credenciamento da empresa',
  otp: 'verificação por código',
  totp: 'autenticação em dois fatores',
  'calendar-suggestions': 'sugestões de agenda',
  'calendar-blockages': 'bloqueios de agenda',
  'calendar-schedules': 'agendamentos',
  'error-reports': 'erros reportados',
  'report-certifiers': 'validadores de laudo',
  'reset-password': 'redefinição de senha',
  announcements: 'comunicados',
  'notice-announcements': 'comunicados de edital',
  avm: 'análise AVM',
  'licensed-companies': 'empresas credenciadas',
  'licensed-company-absences': 'ausências da empresa',
  pathologies: 'patologias',
  'proto-mobile': 'aplicativo',
  'report-customizations': 'personalização de laudo',
  academy: 'Academy',
  cities: 'cidades',
  coupons: 'cupons',
  hubspot: 'atendimento',
  'maintenance-notifications': 'avisos de manutenção',
  'nps-forms': 'pesquisa NPS',
  resource_purchases: 'compras de recursos',
};

// Not every `event_type` comes from the app: the backend emits its own for
// things no request triggers, such as a session being replaced by a newer one.
// Those keys carry no route half and are matched here by their bare name.
const routeGroupLabels: Record<string, string> = {
  session_superseded: 'Sessão encerrada por novo login',
  background: 'Atividade automática',
  bootstrap: 'Carregamento do app',
  login: 'Login',
  logout: 'Logout',
  home: 'Início',
  'home/property/new': 'Nova OS',
  'home/property/{id}/edit': 'Edição da OS',
  'home/property/{id}/schedule': 'Agendamento',
  'home/property/{id}/inspection': 'Vistoria',
  'home/property/{id}/sample': 'Criação de amostra',
  'home/property/{id}/calculation': 'Processando cálculo',
  'home/property/{id}/report': 'Elaboração de laudo',
  'home/property/{id}/revision': 'Revisão e aprovação',
  'home/property/{id}/pept': 'Pendência técnica',
  'home/property/{id}/archived': 'OS arquivada',
  'home/presentation-mode': 'Modo apresentação',
  // Telas do proto-front: chegam por Acesso Delegado, no mesmo formato
  // `<grupo>:<rota>` com `{id}`, porque aquele app envia X-Event-Type com a
  // mesma normalização de rota.
  academy: 'Academy',
  accreditation: 'Credenciamento',
  consent: 'Consentimento de acesso delegado',
  'expired-token': 'Link expirado',
  'first-access': 'Primeiro acesso',
  'inspection-confirmation': 'Confirmação de vistoria',
  'monitored-inspection': 'Vistoria virtual monitorada',
  'open-in-app': 'Abrir no aplicativo',
  'pre-inspection': 'Pré-vistoria',
  'recover-password': 'Recuperar senha',
  'report-validation': 'Validação de laudo',
  satisfaction: 'Pesquisa de satisfação',
  tools: 'Ferramentas',
  validation: 'Validação',
  'virtual-inspection': 'Vistoria virtual',

  // Telas anteriores ao login deste app: não aparecem no menu, de onde saiu o
  // resto deste mapa.
  'create-account': 'Criação de conta',
  'forgot-password': 'Recuperar senha',
  'new-password': 'Redefinição de senha',
  'reset-password': 'Redefinição de senha',

  adjustment: 'Ajustes',
  agenda: 'Agenda',
  announcements: 'Comunicados',
  archived: 'Arquivados',
  configuration: 'Configurações',
  dashboard: 'Dashboard',
  'error-report': 'Erros reportados',
  finance: 'Financeiro',
  forms: 'Formulários',
  notifications: 'Notificações',
  oversight: 'Oversight',
  'oversight/clients': 'Clientes do Oversight',
  'oversight/administrative-users': 'Usuários administrativos',
  'oversight/clients/customer-details/{id}': 'Detalhes do cliente',
  'oversight/administrative-users/details/{id}':
    'Detalhes do usuário administrativo',
  'pre-flight': 'Pre flight',
  // Guarda-chuva: só pega as telas do Hive que ninguém mapeou ainda, para uma
  // tela nova aparecer como si mesma em vez de entrar em Pedidos por engano.
  // As abas seguem o nome que o usuário lê em HIVE_TABS.
  'the-hive': 'The Hive',
  'the-hive/requests': 'Pedidos do Hive',
  'the-hive/licensed': 'Credenciados do Hive',
  'the-hive/licensed/{id}': 'Detalhes do edital',
  'the-hive/licensed/company-details/{id}': 'Detalhes do credenciado',
  // O mesmo rótulo em duas chaves é proposital: a tela é a mesma, alcançada por
  // Credenciados e por Editais, e o agrupamento colapsa por rótulo.
  'the-hive/licensed/company-details/{id}/notices/{id}/request/{id}':
    'Análise de pedido de credenciamento',
  'the-hive/notices': 'Editais',
  'the-hive/notices/new': 'Novo edital',
  'the-hive/notices/{id}': 'Detalhes do edital',
  'the-hive/notices/{id}/add-company': 'Inclusão de empresa no edital',
  'the-hive/notices/{id}/edit': 'Edição de edital',
  'the-hive/notices/{id}/request/{id}': 'Análise de pedido de credenciamento',
  // As 10 telas da Rede Proto são abas de um mesmo painel: percorrê-las é uma
  // atividade só, e cada evento dentro do grupo continua descrito por endpoint.
  'the-hive/rede-proto': 'Dashboard credenciado',
  'user-management': 'Gestão de usuários',
  users: 'Usuários',
  'users-roles': 'Perfis de acesso',
};

// Route groups keep their `{id}` placeholders, so `home/property/{id}/report` and
// `home/property/{id}/report/characteristics` are the same screen to the reader.
// Longest prefix wins; an unmapped group falls back to its raw key so a new
// screen shows up as itself instead of silently merging into another one.
function describeRouteGroup(groupKey: string): string {
  const exact = routeGroupLabels[groupKey];
  if (exact) return exact;

  const [longestPrefix] = Object.keys(routeGroupLabels)
    .filter((candidate) => groupKey.startsWith(`${candidate}/`))
    .sort((a, b) => b.length - a.length);

  return longestPrefix ? routeGroupLabels[longestPrefix] : groupKey;
}

// Credenciamento. Consultado antes de `/work-orders` porque os endpoints de
// edital carregam `/licensed-companies/{id}/work-orders`, que senão seria lido
// como a lista de OS da empresa. `/me/public-notices` é a tela de Credenciamento
// do proto: a empresa olhando os próprios credenciamentos.
function describePublicNoticeEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('public-notices')) return undefined;

  if (path.includes('/me/public-notices'))
    return 'Visualizou os próprios editais';
  if (path.includes('/available'))
    return 'Visualizou novos editais disponíveis';
  if (path.includes('/discredit')) return 'Descredenciou uma empresa';
  if (path.includes('/acceptance-documents')) {
    return method === 'POST'
      ? 'Adicionou documentos para aceite ao edital'
      : 'Visualizou os documentos para aceite do edital';
  }
  if (path.includes('/work-orders')) return 'Visualizou as OS do edital';
  if (path.includes('/payments')) return 'Visualizou os pagamentos do edital';
  if (path.includes('/comments')) {
    return method === 'POST'
      ? 'Comentou no credenciamento da empresa'
      : 'Visualizou os comentários do credenciamento';
  }
  if (path.includes('/analysis-queues')) {
    return 'Visualizou a fila de análise do edital';
  }
  if (path.includes('/operation-flow')) {
    return 'Visualizou o fluxo de operação do edital';
  }

  const noticeMatch = path.match(/public-notices\/(\d+)(?:\/([^?]+))?/);
  if (noticeMatch) {
    const [, noticeId, subpath] = noticeMatch;

    if (!subpath) {
      return (
        noticeEventByMethod(noticeId)[method] ?? `Abriu o edital #${noticeId}`
      );
    }

    const key = subpath.replace(/\/\d+(?=\/|$)/g, '/{id}');
    const [subresource] = key.split('/');

    return (
      noticeSubresourceEvents[key]?.[method] ??
      noticeSubresourceEvents[subresource]?.[method] ??
      `${methodVerb[method] ?? 'Acessou'} ${subresource.replace(
        /-/g,
        ' '
      )} do edital #${noticeId}`
    );
  }

  if (method === 'POST') return 'Criou um edital';

  return 'Visualizou a lista de editais';
}

// Split by domain to stay under the complexity ceiling, and because each of
// these families has its own list-versus-detail traps. Each returns undefined
// when it does not recognise the path, so the caller keeps falling through in
// the original order — that order is load-bearing.
function describeAuthEvent(method: string, path: string): string | undefined {
  if (path.includes('/logout')) return 'Fez logout';

  // The Oversight token is swapped every 15min and the theme is fetched on
  // mount; both hit paths containing /auth/ or /login. Only POST /login is a
  // real login.
  if (path.includes('/auth/exchange')) return 'Renovou a sessão';

  if (path.includes('/login') || path.includes('/auth/')) {
    return method === 'POST'
      ? 'Login realizado'
      : 'Carregou as configurações da empresa';
  }

  return undefined;
}

function describeWorkOrderEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/work-orders')) return undefined;

  if (path.includes('/approve')) return 'Aprovou o resultado';

  if (path.includes('/previous-status')) {
    return 'Voltou a OS para a etapa anterior';
  }
  if (path.includes('/pept')) return 'Enviou a OS para pendência técnica';
  if (path.includes('/reject')) return 'Recusou o resultado';

  const idMatch = path.match(/work-orders\/(\d+)(?:\/([^?]+))?/);
  if (idMatch) {
    const [, workOrderId, subpath] = idMatch;

    if (!subpath) {
      return (
        workOrderEventByMethod(workOrderId)[method] ??
        `Abriu a OS #${workOrderId}`
      );
    }

    const key = subpath.replace(/\/\d+(?=\/|$)/g, '/{id}');
    const [subresource] = key.split('/');
    const event =
      workOrderSubresourceEvents[key]?.[method] ??
      workOrderSubresourceEvents[subresource]?.[method];

    if (event) return event.replace('{id}', workOrderId);
    if (subresource.startsWith('report')) return 'Acessou o laudo';

    return `${methodVerb[method] ?? 'Acessou'} ${subresource.replace(
      /[-_]/g,
      ' '
    )} da OS #${workOrderId}`;
  }

  const statusId = path.match(/work-orders\/in-status\/(\d+)/)?.[1];
  const stage = statusId ? convertStatus(Number(statusId) as Status) : '';
  if (stage) return `Carregou as OS da etapa "${stage}"`;

  const listRoute = path.match(/work-orders\/([^/?]+)/)?.[1];
  const listEvent = listRoute ? workOrderListEvents[listRoute] : undefined;
  if (listEvent) return listEvent;

  if (method === 'POST') return 'Criou uma OS';

  return 'Visualizou a lista de OS';
}

// `inspection-forms` are the saved forms and templates, not inspections. The
// leading slash keeps `/pre-inspection-forms` out of this branch.
function describeFormEvent(method: string, path: string): string | undefined {
  if (!path.includes('/inspection-forms')) return undefined;

  if (method === 'POST') {
    return path.includes('as_template=true')
      ? 'Criou um modelo de formulário'
      : 'Criou um formulário';
  }

  if (method === 'PUT' || method === 'PATCH') return 'Atualizou um formulário';
  if (method === 'DELETE') return 'Removeu um formulário';
  if (/\/inspection-forms\/\d+/.test(path)) return 'Abriu um formulário';

  return path.includes('templates=true')
    ? 'Visualizou os modelos de formulário'
    : 'Visualizou formulários salvos';
}

// The e-mail in the path is never echoed into the timeline.
function describePasswordEvent(
  method: string,
  path: string
): string | undefined {
  if (path.includes('/proto-mobile/password-reset')) return 'Alterou a senha';

  if (!/\/api\/[^/]+\/reset-password(?=\/|\?|$)/.test(path)) return undefined;

  if (method === 'PUT' || method === 'PATCH') return 'Redefiniu a senha';
  if (/reset-password\/[^/?]+/.test(path)) {
    return 'Solicitou a redefinição de senha';
  }

  return 'Validou o link de redefinição de senha';
}

function describeHiveMessageEvent(
  method: string,
  path: string
): string | undefined {
  if (!/\/api\/[^/]+\/hive-messages(?=\/|\?|$)/.test(path)) return undefined;

  if (path.includes('/unread-count')) {
    return 'Verificou o total de mensagens não lidas do Hive';
  }

  return method === 'GET'
    ? 'Visualizou as últimas mensagens do Hive'
    : undefined;
}

function describeNoticeItemEvent(
  method: string,
  path: string
): string | undefined {
  if (/\/api\/[^/]+\/notice-attachments(?=\/|\?|$)/.test(path)) {
    if (method === 'DELETE') return 'Removeu um anexo ou link do edital';
    if (method === 'PATCH' || method === 'PUT') {
      return 'Editou um anexo ou link do edital';
    }
    return undefined;
  }

  if (
    /\/api\/[^/]+\/notice-coverages(?=\/|\?|$)/.test(path) &&
    method === 'DELETE'
  ) {
    return 'Removeu uma área da cobertura do edital';
  }

  return undefined;
}

const criteriaActivityLabels: Record<string, string> = {
  urban: 'urbano',
  rural: 'rural',
};

function describeDefaultCriteriaEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/criterias/default')) return undefined;

  if (method === 'POST') return 'Criou um novo critério';

  const activity = path.match(/[?&]activity=([^&]+)/)?.[1] ?? '';
  const activityLabel = criteriaActivityLabels[activity];

  return activityLabel
    ? `Visualizou os critérios disponíveis (${activityLabel})`
    : 'Visualizou os critérios disponíveis';
}

// The number in `/cub/{n}` is the state picked on the Ajustes screen.
function describeCubEvent(method: string, path: string): string | undefined {
  if (/\/api\/[^/]+\/converted-cub(?=\/|\?|$)/.test(path)) {
    return path.includes('/logic')
      ? 'Atualizou a lógica do CUB transformado'
      : 'Atualizou os valores do CUB transformado';
  }

  const ufId = path.match(/\/api\/[^/]+\/cub\/(\d+)/)?.[1];
  if (!ufId) return undefined;

  const uf = ufAbbreviation[Number(ufId) as RegistrationUf];
  const ofUf = uf ? ` de ${uf}` : '';

  if (path.includes('/converted-cub')) {
    return `Visualizou o CUB transformado${ofUf}`;
  }

  return method === 'PUT'
    ? `Atualizou o CUB${ofUf}`
    : `Visualizou o CUB${ofUf}`;
}

function describeHiveRequestEvent(
  method: string,
  path: string
): string | undefined {
  if (!/\/api\/[^/]+\/hive-requests(?=\/|\?|$)/.test(path)) return undefined;

  if (path.includes('/automatic')) return 'Atribuiu OS automaticamente';
  if (method === 'POST') return 'Atribuiu uma OS a uma empresa credenciada';
  if (method === 'DELETE') return 'Removeu a atribuição da OS';

  return undefined;
}

// Typology groups are the notice's "serviços" on screen.
function describeTypologyEvent(
  method: string,
  path: string
): string | undefined {
  if (/\/api\/[^/]+\/typologies(?=\/|\?|$)/.test(path)) {
    return method === 'PATCH' || method === 'PUT'
      ? 'Editou uma tipologia'
      : undefined;
  }

  if (!/\/api\/[^/]+\/typology-groups(?=\/|\?|$)/.test(path)) return undefined;

  if (path.includes('/typologies')) {
    return method === 'POST'
      ? 'Adicionou uma tipologia a um serviço'
      : undefined;
  }

  const byMethod: Record<string, string> = {
    POST: 'Criou serviços no edital',
    PATCH: 'Editou um serviço do edital',
    PUT: 'Editou um serviço do edital',
    DELETE: 'Removeu um serviço do edital',
  };

  return byMethod[method];
}

function describePhotoEvent(method: string, path: string): string | undefined {
  const [, resource, id, subresource] =
    path.match(
      /\/api\/[^/]+\/(rooms|pictures|pathologies)(?:\/(\d+|select))?(?:\/([^/?]+))?/
    ) ?? [];

  if (!resource) return undefined;

  const byResource: Record<string, Record<string, string>> = {
    rooms: {
      POST: 'Adicionou um cômodo à vistoria',
      GET: 'Abriu um cômodo',
      PUT: 'Editou um cômodo',
      DELETE: 'Removeu um cômodo',
    },
    pictures: {
      POST: 'Enviou uma foto',
      GET: 'Abriu uma foto',
      PUT: 'Editou uma foto',
      DELETE: 'Removeu uma foto',
    },
    pathologies: {
      POST: 'Marcou uma patologia em uma foto',
      PUT: 'Editou uma patologia',
      DELETE: 'Removeu uma patologia',
    },
  };

  if (resource === 'rooms' && subresource === 'pictures') {
    return 'Visualizou as fotos do cômodo';
  }
  if (resource === 'pictures' && id === 'select') {
    return 'Selecionou fotos para o laudo';
  }
  if (resource === 'pictures' && subresource === 'pathologies') {
    return 'Visualizou as patologias de uma foto';
  }
  if (resource === 'pathologies' && method === 'GET' && !subresource) {
    return id ? 'Abriu uma patologia' : 'Visualizou a lista de patologias';
  }
  if (subresource) return undefined;

  return byResource[resource][method];
}

function describeInspectionEvent(
  method: string,
  path: string
): string | undefined {
  if (!/\/api\/[^/]+\/(proto-mobile\/)?inspections(?=\/|\?|$)/.test(path)) {
    return undefined;
  }

  const id = path.match(/inspections\/(\d+)/)?.[1];

  if (!id) {
    return method === 'POST'
      ? 'Agendou uma vistoria'
      : 'Visualizou a lista de vistorias';
  }

  if (path.includes('/proto-mobile/'))
    return `Enviou a selfie da vistoria #${id}`;
  if (path.includes('/selected-pictures')) {
    return 'Visualizou as fotos selecionadas para o laudo';
  }
  if (path.includes('/pictures')) {
    return method === 'DELETE'
      ? `Removeu as fotos da vistoria #${id}`
      : `Visualizou as fotos da vistoria #${id}`;
  }
  if (path.includes('/rooms'))
    return `Visualizou os cômodos da vistoria #${id}`;
  if (method === 'PUT' || method === 'PATCH') {
    return `Atualizou a vistoria #${id}`;
  }
  if (method === 'DELETE') {
    return `Cancelou o agendamento da vistoria #${id}`;
  }

  return `Abriu a vistoria #${id}`;
}

function describeNoticeCompanyEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/notice-companies')) return undefined;

  const subpath = path.match(/notice-companies\/\d+\/([^?]+)/)?.[1];
  const verb = methodVerb[method] ?? 'Acessou';

  if (subpath) {
    const key = subpath.replace(/\/\d+(?=\/|$)/g, '/{id}');
    const event = noticeCompanySubresourceEvents[key]?.[method];

    if (event) return event;

    const [subresource] = key.split('/');
    return `${verb} ${subresource.replace(/[-_]/g, ' ')} do credenciamento`;
  }

  return method === 'GET'
    ? 'Abriu o credenciamento'
    : `${verb} o credenciamento`;
}

// Only paths that start with `/licensed-companies`; the notice ones nest it.
function describeLicensedCompanyEvent(
  method: string,
  path: string
): string | undefined {
  if (!/\/api\/[^/]+\/licensed-companies(?=\/|\?|$)/.test(path)) {
    return undefined;
  }

  const id = path.match(/licensed-companies\/(\d+)/)?.[1];

  if (!id) {
    return method === 'POST'
      ? 'Credenciou uma empresa'
      : 'Visualizou a lista de empresas credenciadas';
  }

  if (path.includes('/observations')) {
    return method === 'POST'
      ? `Adicionou uma observação à empresa credenciada #${id}`
      : `Visualizou as observações da empresa credenciada #${id}`;
  }
  if (path.includes('/comments')) {
    return `Visualizou o histórico de comentários da empresa credenciada #${id}`;
  }
  if (path.includes('/absences')) {
    return `Visualizou as ausências da empresa credenciada #${id}`;
  }
  if (path.includes('/public-notices')) {
    return `Visualizou os editais da empresa credenciada #${id}`;
  }
  if (method === 'PATCH' || method === 'PUT') {
    return `Atualizou os dados da empresa credenciada #${id}`;
  }

  return `Abriu a empresa credenciada #${id}`;
}

// `/companies/me` stays generic: describeHomeEvent relies on that exact text.
function describeCompanyEvent(
  method: string,
  path: string
): string | undefined {
  if (!/\/api\/[^/]+\/companies(?=\/|\?|$)/.test(path)) return undefined;

  if (path.includes('/first-password')) {
    return method === 'POST'
      ? 'Cadastrou a senha de primeiro acesso'
      : 'Solicitou o código de primeiro acesso';
  }
  if (path.includes('/companies/document/')) {
    return 'Buscou uma empresa pelo CNPJ';
  }
  if (path.includes('/notice-announcements/unread-count')) {
    return 'Verificou os comunicados de edital não lidos';
  }

  const statsId = path.match(/companies\/(\d+)\/company-stats/)?.[1];
  if (statsId) return `Visualizou as estatísticas da empresa #${statsId}`;

  if (/\/companies(\?|$)/.test(path)) return 'Visualizou a lista de empresas';

  return undefined;
}

function describeHivePaymentEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/hive-payments')) return undefined;

  if (path.includes('/hive-payments/summary')) {
    return 'Visualizou o resumo financeiro';
  }
  if (path.includes('/hive-payments/export-csv')) {
    return 'Exportou os pagamentos em CSV';
  }

  const id = path.match(/hive-payments\/(\d+)/)?.[1];

  if (!id) return 'Visualizou a lista de pagamentos do Hive';

  if (path.includes('/status')) return `Alterou o status do pagamento #${id}`;
  if (path.includes('/files')) {
    return method === 'DELETE'
      ? `Removeu um arquivo do pagamento #${id}`
      : `Anexou um arquivo ao pagamento #${id}`;
  }

  return `Abriu o pagamento #${id}`;
}

// `/report` completes the work order and delivers the report to the source.
function describePreFlightEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/preflight-requests')) return undefined;

  if (path.includes('/preflight-requests/companies')) {
    return 'Visualizou as empresas do Pre flight';
  }

  const id = path.match(/preflight-requests\/(\d+)/)?.[1];

  if (!id) return 'Visualizou a lista de solicitações do Pre flight';

  if (path.includes('/work-order')) {
    return `Atualizou os dados da OS da solicitação #${id}`;
  }
  if (path.includes('/approve')) return `Aprovou a solicitação #${id}`;
  if (path.includes('/reject')) return `Reprovou a solicitação #${id}`;
  if (path.includes('/report')) return `Entregou o laudo da solicitação #${id}`;
  if (path.includes('/documents')) {
    return method === 'DELETE'
      ? `Removeu um documento da solicitação #${id}`
      : `Anexou um documento à solicitação #${id}`;
  }

  return `Abriu a solicitação #${id} do Pre flight`;
}

// Every edit PATCHes the title before the cards, so it means "saved the edit".
function describeAnnouncementEvent(
  method: string,
  path: string
): string | undefined {
  if (path.includes('/announcement-cards')) {
    return method === 'DELETE'
      ? 'Removeu um card de comunicado'
      : 'Atualizou um card de comunicado';
  }

  if (!/\/announcements(?=\/|\?|$)/.test(path)) return undefined;
  if (path.includes('/announcements/active')) {
    return 'Verificou se há comunicado ativo';
  }

  const id = path.match(/announcements\/(\d+)/)?.[1];

  if (!id) {
    return method === 'POST'
      ? 'Criou um comunicado'
      : 'Visualizou a lista de comunicados';
  }

  if (path.includes('/cards/order')) {
    return `Reordenou os cards do comunicado #${id}`;
  }
  if (path.includes('/cards')) {
    return method === 'POST'
      ? `Adicionou um card ao comunicado #${id}`
      : `Visualizou os cards do comunicado #${id}`;
  }
  if (path.includes('/status')) return `Alterou o status do comunicado #${id}`;
  if (method === 'PATCH' || method === 'PUT') {
    return `Salvou a edição do comunicado #${id}`;
  }
  if (method === 'DELETE') return `Removeu o comunicado #${id}`;

  return `Abriu o comunicado #${id}`;
}

function describeErrorReportEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/error-reports')) return undefined;

  if (path.includes('/activate-element')) {
    return 'Marcou ausência de erros e reativou o elemento';
  }

  const reportId = path.match(/error-reports\/(\d+)/)?.[1];

  if (!reportId) {
    return method === 'POST'
      ? 'Reportou um erro'
      : 'Visualizou a lista de erros reportados';
  }

  if (path.includes('/finish'))
    return `Finalizou o ticket de erro #${reportId}`;
  if (method === 'PUT' || method === 'PATCH') {
    return `Atualizou o erro reportado #${reportId}`;
  }
  if (method === 'DELETE') return `Removeu o erro reportado #${reportId}`;

  return `Abriu o erro reportado #${reportId}`;
}

function describeOversightEvent(
  method: string,
  path: string
): string | undefined {
  // `/clients` serves the list, one client, and that client's companies, so
  // the id has to be matched before falling back to the list wording.
  if (path.includes('/clients')) {
    if (/\/clients\/\d+/.test(path)) {
      if (path.includes('/companies'))
        return 'Visualizou as empresas do cliente';

      return 'Abriu os dados cliente';
    }

    if (method === 'GET') return 'Visualizou a lista de clientes do Oversight';
  }

  if (path.includes('/active-delegation')) {
    return 'Verificou se há acesso delegado ativo';
  }

  if (path.includes('/pending-consent')) {
    return 'Verificou se há consentimento de acesso delegado pendente';
  }

  if (path.includes('/consent/') && path.includes('/respond')) {
    return 'Respondeu à solicitação de acesso delegado';
  }

  // Os segmentos de ação levam barra para não casar com query string:
  // "?status=pending_consent" contém "end", "?order_by=started_at" contém
  // "start".
  if (path.includes('delegated')) {
    if (path.includes('/start')) return 'Iniciou um acesso delegado';
    if (path.includes('/cancel')) {
      return 'Cancelou a solicitação de acesso delegado';
    }
    if (path.includes('/end')) return 'Encerrou o acesso delegado';
    if (method === 'POST') return 'Criou um acesso delegado';
    if (/\/delegated-sessions\/\d+/.test(path)) {
      return 'Abriu um acesso delegado';
    }
    if (method === 'GET') return 'Visualizou a lista de acessos delegados';
  }

  if (path.includes('sessions') && method === 'GET') {
    return 'Visualizou a lista de sessões';
  }

  if (path.includes('session_events') && method === 'GET') {
    return 'Visualizou a lista de eventos de sessão';
  }

  return undefined;
}

// Caminhos em que o primeiro segmento esconde o recurso: `companies/me/plan`
// não é "dados da empresa". Prefixo mais longo vence, como em routeGroupLabels,
// e cada entrada é uma decisão explícita — nada muda de rótulo sozinho.
const endpointLabels: Record<string, string> = {
  'companies/evaluation-config': 'a configuração de avaliação',
  'companies/me/address': 'o endereço da empresa',
  'companies/me/billing-portal': 'o portal de cobrança',
  'companies/me/default-settings': 'as configurações padrão da empresa',
  'companies/me/hive-requests': 'as solicitações do Hive',
  'companies/me/notice-term-status': 'o termo de edital',
  'companies/me/payment-term-status': 'o termo de pagamento',
  'companies/me/plan': 'o plano da empresa',
  'companies/me/users': 'os usuários da empresa',
  'plans/me': 'o próprio plano',
  'rooms/{id}/pictures': 'as fotos do cômodo',
  'users/{id}': 'dados do usuário',
  'users/me/data': 'os próprios dados',
  'users/me/idwall-number': 'a validação de identidade',
  'users/registration-status': 'o status do cadastro',
};

function describeEndpointResource(endpoint: string): string | undefined {
  const path = endpoint
    .split('?')[0]
    .toLowerCase()
    .replace(/^\/?api\/[^/]+\//, '')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');

  const exact = endpointLabels[path];
  if (exact) return exact;

  const [longest] = Object.keys(endpointLabels)
    .filter((candidate) => path.startsWith(`${candidate}/`))
    .sort((a, b) => b.length - a.length);

  return longest ? endpointLabels[longest] : undefined;
}

function describeSessionEvent(method: string, endpoint: string): string {
  const path = endpoint.toLowerCase();

  const auth = describeAuthEvent(method, path);
  if (auth) return auth;

  const licensedCompany = describeLicensedCompanyEvent(method, path);
  if (licensedCompany) return licensedCompany;

  const notice = describePublicNoticeEvent(method, path);
  if (notice) return notice;

  const workOrder = describeWorkOrderEvent(method, path);
  if (workOrder) return workOrder;

  if (path.includes('sample')) {
    const sampleMatch = path.match(/\/samples\/(\d+)(?:\/([^/?]+))?/);
    const sampleEvent =
      sampleMatch &&
      (sampleMatch[2]
        ? sampleSubresourceEvents[sampleMatch[2]]?.[method]
        : sampleEventByMethod[method]);

    if (sampleMatch && sampleEvent) {
      return sampleEvent.replace('{id}', sampleMatch[1]);
    }

    if (method === 'POST') {
      return /\/api\/[^/]+\/samples(\?|$)/.test(path)
        ? 'Adicionou um elemento manualmente'
        : 'Criou uma amostra';
    }
    if (method === 'DELETE') return 'Removeu uma amostra';
    if (method === 'PUT' || method === 'PATCH') return 'Atualizou uma amostra';
    return 'Visualizou amostras';
  }

  const form = describeFormEvent(method, path);
  if (form) return form;

  const inspection = describeInspectionEvent(method, path);
  if (inspection) return inspection;

  const photo = describePhotoEvent(method, path);
  if (photo) return photo;

  const typology = describeTypologyEvent(method, path);
  if (typology) return typology;

  const hiveRequest = describeHiveRequestEvent(method, path);
  if (hiveRequest) return hiveRequest;

  const cub = describeCubEvent(method, path);
  if (cub) return cub;

  const defaultCriteria = describeDefaultCriteriaEvent(method, path);
  if (defaultCriteria) return defaultCriteria;

  const noticeItem = describeNoticeItemEvent(method, path);
  if (noticeItem) return noticeItem;

  const hiveMessage = describeHiveMessageEvent(method, path);
  if (hiveMessage) return hiveMessage;

  const password = describePasswordEvent(method, path);
  if (password) return password;

  if (path.includes('inspection')) {
    if (method === 'POST') return 'Criou uma vistoria';
    return 'Visualizou vistorias';
  }

  const oversight = describeOversightEvent(method, path);
  if (oversight) return oversight;

  const errorReport = describeErrorReportEvent(method, path);
  if (errorReport) return errorReport;

  const announcement = describeAnnouncementEvent(method, path);
  if (announcement) return announcement;

  const preFlight = describePreFlightEvent(method, path);
  if (preFlight) return preFlight;

  const hivePayment = describeHivePaymentEvent(method, path);
  if (hivePayment) return hivePayment;

  const company = describeCompanyEvent(method, path);
  if (company) return company;

  const noticeCompany = describeNoticeCompanyEvent(method, path);
  if (noticeCompany) return noticeCompany;

  // `/companies/me/credits` cairia no fallback como `companies` ("dados da
  // empresa"), escondendo que a consulta foi ao saldo de créditos.
  if (path.includes('/credits')) return 'Carregou os créditos da empresa';

  const verb = methodVerb[method] ?? 'Acessou';
  const segMatch = endpoint.match(/\/api\/[^/]+\/([^/?]+)/i);
  const key = segMatch ? segMatch[1].toLowerCase() : '';
  const resource =
    describeEndpointResource(endpoint) ??
    resourceLabels[key] ??
    (key ? key.replace(/-/g, ' ') : 'um recurso');

  return `${verb} ${resource}`;
}

// No bootstrap o app só busca dados para montar a tela; escritas mantêm o verbo.
const readVerbPattern = /^(Visualizou|Abriu|Acessou)\b/;

function describeBootstrapEvent(method: string, endpoint: string): string {
  return describeSessionEvent(method, endpoint).replace(
    readVerbPattern,
    'Carregou'
  );
}

// No Início o app busca os dados do usuário e da empresa só para montar a tela.
const homeLoadedResources = [
  endpointLabels['users/{id}'],
  resourceLabels.companies,
].map((resource) => `${methodVerb.GET} ${resource}`);

function describeHomeEvent(method: string, endpoint: string): string {
  const description = describeSessionEvent(method, endpoint);

  return method === 'GET' && homeLoadedResources.includes(description)
    ? describeBootstrapEvent(method, endpoint)
    : description;
}

export {
  describeBootstrapEvent,
  describeHomeEvent,
  describeRouteGroup,
  describeSessionEvent,
};
